import type { Action, Agent, Hook } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import { ExportOption } from '@/components/flow';
import { HOOK_EVALUATE_PREDICTION } from '@/constants';
import { toAgentId, useCreateAction, useCreateHook, useUpdateAgent } from '@/hooks/api';

export type UseAddExportOnSuccess = ({
  action,
  autoHook,
  reviewHook,
  agent,
}: {
  action: Action;
  autoHook: Hook;
  reviewHook: Hook;
  agent: Agent;
}) => void;

export type UseAddExportOnError = ({ error }: { error: Error }) => void;
export type UseAddExportCallbackArgs = {
  agentId: string;
  exportOption: ExportOption;
  onSuccess?: UseAddExportOnSuccess;
  onError?: UseAddExportOnError;
};
export type UseAddExportCallback = ({ agentId, exportOption, onSuccess, onError }: UseAddExportCallbackArgs) => void;

export type UseAddExportOpts = {
  onSuccess?: UseAddExportOnSuccess;
  onError?: UseAddExportOnError;
};

export const useAddExport = ({ onSuccess: onSuccessFnBase, onError: onErrorFnBase }: UseAddExportOpts = {}) => {
  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();
  const {
    mutateAsync: createAction,
    isPending: isPendingCreateAction,
    isSuccess: isSuccessCreateAction,
    error: errorCreateAction,
  } = useCreateAction();
  const {
    mutateAsync: createHook,
    isPending: isPendingCreateHook,
    isSuccess: isSuccessCreateHook,
    error: errorCreateHook,
  } = useCreateHook();

  const addExport: UseAddExportCallback = useCallback(
    async ({ agentId, exportOption, onSuccess: onSuccessFn, onError: onErrorFn }: UseAddExportCallbackArgs) => {
      const onSuccess: UseAddExportOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };

      const onError: UseAddExportOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      try {
        const action = await createAction({
          config: typeof exportOption.config === 'function' ? exportOption.config() : exportOption.config,
          functionId: exportOption.functionId,
          name: exportOption.name,
        });

        const [reviewHook, autoHook] = await Promise.all([
          // Hook triggering when validation task has succeeded
          createHook({
            config: {},
            trigger: 'ValidationTask has Completed',
            trueActionId: action.actionId,
          }),

          // Hook triggering when prediction is created and passed auto validation
          createHook({
            functionId: HOOK_EVALUATE_PREDICTION,
            config: {},
            trigger: 'Prediction is Created',
            trueActionId: action.actionId,
          }),
        ]);

        const agent = await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds, action.actionId, reviewHook.hookId, autoHook.hookId],
          }),
        });

        onSuccess?.({ action, reviewHook, autoHook, agent });

        return action;
      } catch (error) {
        if (error instanceof Error) {
          onError?.({ error });
        }
      }
    },
    [createAction, createHook, onErrorFnBase, onSuccessFnBase, updateAgent]
  );

  return {
    addExport,
    isPending: isPendingCreateAction || isPendingCreateHook || isPendingUpdateAgent,
    isSuccess: isSuccessCreateAction && isSuccessCreateHook && isSuccessUpdateAgent,
    errors: [errorCreateAction, errorCreateHook, errorUpdateAgent].filter((e) => !!e),
  };
};
